/**
 * 商品分类自定义路由工具（PC / H5 共用同一套 slug 规则）
 * 使用 catch-all 路由 + beforeEnter 解析分类，避免 addRoute 时序问题
 */
import store from "../store/index";
import apis from "../api/apis";

let categoryDataPromise = null;

// 运费试算静态页路径（encodeURI 后 Vue Router 3 才能稳定匹配，alias 兼容带 í 的链接）
export const LOGISTICS_STATIC_PAGE_PATH = encodeURI("/Prezzo-della-logística");
export const LOGISTICS_STATIC_PAGE_ROUTE_NAME = "internationalLogisticsStaticPage";

/** 判断是否为运费试算静态页路径 - 用于 catch-all 兜底，避免误跳首页或死循环 */
export function getStaticPageFallbackPath(path) {
  const normalized = normalizeShopGroupPath(path);
  const candidates = [
    normalizeShopGroupPath(LOGISTICS_STATIC_PAGE_PATH),
    normalizeShopGroupPath("/Prezzo-della-logística"),
    normalizeShopGroupPath("/Prezzo-della-logistica"),
  ];
  return candidates.includes(normalized) ? LOGISTICS_STATIC_PAGE_PATH : null;
}

/** 是否为运费试算静态页路径（仅判断，不返回跳转目标） */
export function isLogisticsStaticPagePath(path) {
  return !!getStaticPageFallbackPath(path);
}

/** 判断 store 中是否已有有效分类数据 */
function isValidShopGroupList(list) {
  return !!(
    list &&
    list.length &&
    list[0] &&
    list[0].id &&
    list[0].translation_name
  );
}

/** 标准 slug */
export function translationNameToSlug(name) {
  return normalizeSlugSegment(
    (name || "").replace(/ /g, "-").replace(/\//g, "-"),
  );
}

export function legacyTranslationNameToSlug(name) {
  return encodeURIComponent((name || "").replace(/ /g, "-"));
}

export function normalizeSlugSegment(segment) {
  let s = String(segment || "");
  try {
    s = decodeURIComponent(s);
  } catch (e) {}
  return s
    .replace(/%2F/gi, "-")
    .replace(/ /g, "-")
    .replace(/\//g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

export function getCategorySlugVariants(category) {
  const name = category?.translation_name || "";
  const variants = [
    translationNameToSlug(name),
    normalizeSlugSegment(legacyTranslationNameToSlug(name)),
    normalizeSlugSegment(name.replace(/ /g, "-")),
  ];
  return [...new Set(variants.filter(Boolean))];
}

export function categorySlugMatches(category, segment) {
  const normalizedSegment = normalizeSlugSegment(segment);
  return getCategorySlugVariants(category).includes(normalizedSegment);
}

export function normalizeShopGroupPath(path) {
  const pathname = String(path || "")
    .split("?")[0]
    .split("#")[0];
  let decoded = pathname;
  try {
    decoded = decodeURIComponent(pathname);
  } catch (e) {}
  const segments = decoded
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean)
    .map((segment) => normalizeSlugSegment(segment))
    .filter(Boolean);
  const joined = segments.length ? "/" + segments.join("/") : "/";
  // 统一 Unicode 形式，避免 logística 等带重音路径 NFD/NFC 不一致导致静态路由匹配失败
  return joined.normalize("NFC");
}

/** 从 vue-router 的 to 对象提取完整 path（兼容 catch-all 的 pathMatch 参数） */
export function getPathFromRoute(to) {
  if (!to) return "/";
  const pathMatch = to.params && to.params.pathMatch;
  if (pathMatch != null && pathMatch !== "") {
    const parts = Array.isArray(pathMatch)
      ? pathMatch
      : String(pathMatch).split("/");
    const joined = parts.filter(Boolean).join("/");
    if (joined) {
      return normalizeShopGroupPath("/" + joined);
    }
  }
  return normalizeShopGroupPath(to.path || "/");
}

function findCategoryByCanonicalPath(categories, targetPath, ancestors = []) {
  for (const category of categories || []) {
    const canonicalPath = getShopGroupCategoryPath(category, ancestors);
    if (canonicalPath === targetPath) {
      return {
        category,
        canonicalPath,
        ancestors,
      };
    }
    if (category.childs && category.childs.length) {
      const found = findCategoryByCanonicalPath(category.childs, targetPath, [
        ...ancestors,
        category,
      ]);
      if (found) return found;
    }
  }
  return null;
}

export function isShopGroupRoute(route) {
  return !!(route && route.meta && route.meta.isShopGroupRoute);
}

export function isCommoditySearchRoute(route) {
  if (!route) return false;
  return route.name === "CommoditySearch" || isShopGroupRoute(route);
}

export function getShopGroupCategoryPath(category, ancestors = []) {
  const segments = [...ancestors, category].map((item) =>
    translationNameToSlug(item.translation_name),
  );
  return "/" + segments.join("/");
}

export function findCategoryPathInShopGroup(
  categories,
  { id, chinese_name, translation_name } = {},
  ancestors = [],
) {
  for (const category of categories || []) {
    const matched =
      (id && category.id === id) ||
      (chinese_name && category.chinese_name === chinese_name) ||
      (translation_name && category.translation_name === translation_name);

    if (matched) {
      return getShopGroupCategoryPath(category, ancestors);
    }

    if (category.childs && category.childs.length) {
      const path = findCategoryPathInShopGroup(
        category.childs,
        { id, chinese_name, translation_name },
        [...ancestors, category],
      );
      if (path) return path;
    }
  }
  return null;
}

export function findCategoryByPath(categories, path) {
  const segments = normalizeShopGroupPath(path)
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean);

  if (!segments.length) return null;

  let currentList = categories || [];
  let found = null;

  for (const segment of segments) {
    found = currentList.find((item) => categorySlugMatches(item, segment));
    if (!found) return null;
    currentList = found.childs || [];
  }

  return found;
}

export function resolveShopGroupPath(categories, rawPath) {
  const normalizedPath = normalizeShopGroupPath(rawPath);
  const segments = normalizedPath
    .replace(/^\//, "")
    .split("/")
    .filter(Boolean);
  if (!segments.length) return null;

  let currentList = categories || [];
  const ancestorChain = [];

  for (const segment of segments) {
    const found = currentList.find((item) =>
      categorySlugMatches(item, segment),
    );
    if (!found) {
      return findCategoryByCanonicalPath(categories, normalizedPath);
    }
    ancestorChain.push(found);
    currentList = found.childs || [];
  }

  const category = ancestorChain[ancestorChain.length - 1];
  const canonicalPath = getShopGroupCategoryPath(
    category,
    ancestorChain.slice(0, -1),
  );

  return { category, canonicalPath, ancestors: ancestorChain.slice(0, -1) };
}

/**
 * 拉取 getShopGroup 分类树；store 已有数据或请求进行中时复用，避免重复请求
 */
export function ensureShopGroupCategories() {
  const goodsList = store.state.goodsList;
  if (isValidShopGroupList(goodsList)) {
    return Promise.resolve(goodsList);
  }
  if (categoryDataPromise) {
    return categoryDataPromise;
  }

  categoryDataPromise = apis
    .getShopGroup()
    .then((result) => {
      const list = (result && result.data) || [];
      if (list.length) {
        store.commit("getgoodsList", list);
      }
      return list;
    })
    .catch(() => {
      categoryDataPromise = null;
      return [];
    });
  return categoryDataPromise;
}

/** catch-all 路由 beforeEnter：解析分类并注入 meta */
export function shopGroupBeforeEnter(to, from, next) {
  const navigationPath = getPathFromRoute(to);
  ensureShopGroupCategories().then(() => {
    const categories = store.state.goodsList || [];
    const resolved = resolveShopGroupPath(categories, navigationPath);
    if (!resolved?.category) {
      // 用 route name 跳转，避免 encodeURI 路径与 alias 之间 beforeEnter 死循环
      if (isLogisticsStaticPagePath(navigationPath)) {
        next({ name: LOGISTICS_STATIC_PAGE_ROUTE_NAME, replace: true });
        return;
      }
      next({ path: "/", replace: true });
      return;
    }
    if (resolved.canonicalPath !== navigationPath) {
      next({ path: resolved.canonicalPath, replace: true });
      return;
    }
    to.meta.shopGroupCategory = {
      id: resolved.category.id,
      translation_name: resolved.category.translation_name,
      chinese_name: resolved.category.chinese_name,
    };
    next();
  });
}

/**
 * 分类 catch-all 路由（必须放在 routes 数组最后）
 * @param {Function} loadComponent 商品搜索页组件 loader
 * @param {boolean} isVueRouter4
 */
export function getShopGroupCatchAllRoute(loadComponent, isVueRouter4 = true) {
  return {
    name: "shopGroupCatchAll",
    path: isVueRouter4 ? "/:pathMatch(.*)*" : "*",
    component: loadComponent,
    meta: {
      keepAlive: true,
      buDaiRakumart: true,
      isShopGroupRoute: true,
    },
    beforeEnter: shopGroupBeforeEnter,
  };
}

export function getShopGroupRouteRedirect(router, categories, to) {
  if (to.matched.length) return null;

  const resolved = resolveShopGroupPath(categories, to.path);
  if (resolved?.canonicalPath) {
    return { path: resolved.canonicalPath, replace: true };
  }

  const normalized = normalizeShopGroupPath(to.path);
  if (normalized !== to.path) {
    const retry = router.resolve(normalized);
    if (retry.matched.length) {
      return { path: normalized, replace: true };
    }
  }

  return null;
}
